import React, { useState } from "react";
import {
  Box,
  Center,
  FormControl,
  FormLabel,
  Select,
  VStack,
  FormErrorMessage,
  Text,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import CustomInput from "../../components/UI/CustomInput";
import CustomButton from "../../components/UI/CustomButton";
import { validateEmail } from "../../helpers/utils";
import { motion } from "framer-motion";
import { useFormTheme } from "../../hooks/useFormTheme";
import AuthLogo from "../../components/UI/AuthLogo";
import { useAuth } from "../../context/AuthContext";
import { USER_EMAIL_KEY } from "../../helpers/constant";
import FormErrorBox from "../../components/UI/FormErrorBox";
import { useTranslation } from "react-i18next";
import { createUser } from "../../services/auth";
import { getLocalizedAuthErrorMessage } from "../../helpers/authErrors";

const cities = ["Douala", "Yaounde", "Bafoussam"];

const registerFields = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "password",
  "confirmPassword",
] as const;

type RegisterField = (typeof registerFields)[number];

const MotionBox = motion(Box);

const authCardTransition = {
  layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
};

const RegisterPage: React.FC = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    city: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { i18n, t } = useTranslation();
  const { signUpHandler, isLoading } = useAuth();

  const fieldLabelMap: Record<RegisterField, string> = {
    firstName: String(t("auth.register.firstName")),
    lastName: String(t("auth.register.lastName")),
    email: String(t("auth.register.email")),
    phone: String(t("auth.register.phone")),
    password: String(t("auth.register.password")),
    confirmPassword: String(t("auth.register.confirmPassword")),
  };

  const fieldPlaceholderMap: Record<RegisterField, string> = {
    firstName: String(t("auth.register.firstNamePlaceholder")),
    lastName: String(t("auth.register.lastNamePlaceholder")),
    email: String(t("auth.register.emailPlaceholder")),
    phone: String(t("auth.register.phonePlaceholder")),
    password: String(t("auth.register.passwordPlaceholder")),
    confirmPassword: String(t("auth.register.confirmPasswordPlaceholder")),
  };

  const validate = (field?: string, value?: string): boolean => {
    const newErrors: Record<string, string> =
      field && hasSubmitted ? { ...errors } : {};
    const f = {
      ...form,
      ...(value !== undefined && field ? { [field]: value } : {}),
    };

    const check = (
      name: keyof typeof form,
      condition: boolean,
      msg: string,
    ) => {
      if (!field || field === name)
        condition ? (newErrors[name] = msg) : delete newErrors[name];
    };

    check(
      "firstName",
      !f.firstName,
      String(t("auth.common.firstNameRequired")),
    );
    check("lastName", !f.lastName, String(t("auth.common.lastNameRequired")));
    check("city", !f.city, String(t("auth.common.cityRequired")));
    check(
      "email",
      !validateEmail(f.email),
      String(t("auth.common.invalidEmail")),
    );

    check(
      "phone",
      !/^\+?\d{8,15}$/.test(f.phone),
      String(t("auth.common.invalidPhone")),
    );
    check(
      "password",
      f.password.length < 6,
      String(t("auth.common.passwordTooShort")),
    );

    if (!field || field === "password" || field === "confirmPassword") {
      if (f.confirmPassword !== f.password) {
        newErrors.confirmPassword = String(t("auth.common.passwordsMustMatch"));
      } else {
        delete newErrors.confirmPassword;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const username = `${form.firstName} ${form.lastName}`.trim();
      const payload = {
        username,
        email: form.email,
        password: form.password,
        phoneNumbers: [form.phone],
        name: {
          givenName: form.firstName,
          familyName: form.lastName,
        },
        city: form.city,
      };
      const userID = await signUpHandler(
        payload.username,
        payload.email,
        payload.password,
        payload.phoneNumbers[0],
        payload.name.givenName,
        payload.name.familyName,
        payload.city,
        i18n.language?.toLowerCase().split("-")[0] || "en",
      );

      await createUser({
        userID,
        firstname: payload.name.givenName,
        lastname: payload.name.familyName,
        phoneNumber: payload.phoneNumbers[0],
        email: payload.email,
        city: payload.city.toLowerCase(),
        lang: i18n.language?.toLowerCase().split("-")[0] || "en",
      });

      localStorage.setItem(USER_EMAIL_KEY, form.email);

      toast({
        status: "success",
        title: t("auth.register.successTitle"),
        description: t("auth.register.successDescription"),
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      navigate("/verify-code", {
        state: {
          email: form.email,
        },
      });
    } catch (error) {
      toast({
        title: t("auth.register.errorTitle"),
        description: getLocalizedAuthErrorMessage(error, t),
        status: "error",
        duration: 3500,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };
  const {
    bg,
    errorColor,
    linkColor,
    pageBg,
    textColor,
    lightGradient,
    darkGradient,
  } = useFormTheme();

  return (
    <Center position="relative" minH="100vh">
      <Box
        position="fixed"
        top={0}
        left={0}
        zIndex={-1}
        minH="100vh"
        bg={pageBg}
        w="100vw"
        bgGradient={lightGradient}
        _dark={{ bgGradient: darkGradient }}
      />
      <MotionBox
        maxW={{ base: "90%", md: "400px", "2xl": "450px" }}
        px={{ base: "24px", md: "40px" }}
        w="full"
        bg={bg}
        boxShadow="md"
        borderRadius="md"
        py="44px"
        mt="80px"
        mb="40px"
        layout
        transition={authCardTransition}
      >
        <AuthLogo />

        <Center mb={6}>
          <Text fontSize="16px" fontWeight="medium" color={textColor}>
            {t("auth.register.title")}
          </Text>
        </Center>

        <form onSubmit={handleSubmit}>
          <VStack spacing={{ base: "24px", md: "32px" }} align="stretch">
            {registerFields.map((field) => (
              <FormControl key={field} isInvalid={!!errors[field]}>
                <FormLabel textTransform="capitalize">
                  {fieldLabelMap[field]}
                </FormLabel>
                <CustomInput
                  type={
                    field.toLowerCase().includes("password")
                      ? "password"
                      : field.toLowerCase() === "email"
                        ? "email"
                        : "text"
                  }
                  error={!!errors[field]}
                  placeholder={fieldPlaceholderMap[field]}
                  value={(form as any)[field]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, [field]: value });
                    if (hasSubmitted) {
                      validate(field, value);
                    }
                  }}
                />
                <FormErrorMessage color={errorColor}>
                  {errors[field]}
                </FormErrorMessage>
              </FormControl>
            ))}

            {/* City dropdown */}
            <FormControl isInvalid={!!errors.city}>
              <FormLabel>{t("auth.register.city")}</FormLabel>
              <Select
                placeholder={String(t("auth.register.cityPlaceholder"))}
                value={form.city}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm({ ...form, city: value });
                  if (hasSubmitted) {
                    validate("city", value);
                  }
                }}
              >
                {cities.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
              <FormErrorMessage color={errorColor}>
                {errors.city}
              </FormErrorMessage>
            </FormControl>

            <FormErrorBox errors={Object.values(errors)} />

            <CustomButton type="submit" isDisabled={loading || isLoading}>
              {loading || isLoading ? (
                <Spinner size="sm" />
              ) : (
                t("auth.register.submit")
              )}
            </CustomButton>

            <Center>
              <Text fontSize="14px" color={linkColor}>
                {t("auth.register.alreadyHaveAccount")}{" "}
                <Text
                  as="span"
                  textDecor="underline"
                  cursor="pointer"
                  color={linkColor}
                  onClick={() => navigate("/login")}
                >
                  {t("auth.register.login")}
                </Text>
              </Text>
            </Center>
          </VStack>
        </form>
      </MotionBox>
    </Center>
  );
};

export default RegisterPage;
